/**
 * Authentication helper functions
 */

/**
 * Get the JWT token from localStorage
 */
export const getAuthToken = () => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('postaway_auth_token');
};

/**
 * Get user data from localStorage
 */
export const getUserData = () => {
  if (typeof window === 'undefined') return null;
  try {
    const userData = localStorage.getItem('postaway_user_data');
    return userData ? JSON.parse(userData) : null;
  } catch (e) {
    console.error('Failed to parse user data:', e);
    return null;
  }
};

export const persistAuthResult = ({ email, token, user }) => {
  if (typeof window === 'undefined') return;
  if (email) localStorage.setItem('postaway_email', email);
  if (token) localStorage.setItem('postaway_auth_token', token);
  if (user) localStorage.setItem('postaway_user_data', JSON.stringify(user));

  const activePersona = user?.activePersona || user?.userType || (Array.isArray(user?.roles) ? user.roles[0] : null);
  if (activePersona) {
    localStorage.setItem('postaway_user_type', activePersona);
  }
  window.dispatchEvent(new Event('postaway:persona-changed'));
};

/**
 * Decode JWT token (for debugging - doesn't verify signature)
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (e) {
    console.error('Failed to decode token:', e);
    return null;
  }
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  const now = Date.now() / 1000;
  return decoded.exp < now;
};

/**
 * Clear all auth data from localStorage
 */
export const clearAuthData = () => {
  if (typeof window === 'undefined') return;
  
  localStorage.removeItem('postaway_auth_token');
  localStorage.removeItem('postaway_email');
  localStorage.removeItem('postaway_user_type');
  localStorage.removeItem('postaway_user_data');
  localStorage.removeItem('postaway_session');
  
  // Clear all sessionStorage
  try {
    sessionStorage.clear();
  } catch (e) {
    console.warn('Failed to clear sessionStorage:', e);
  }
  
  // Clear all cookies (including HttpOnly cookies by setting expiration)
  try {
    document.cookie.split(";").forEach((c) => {
      const eqPos = c.indexOf("=");
      const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
      // Clear cookie by setting it to expire in the past
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname};`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname};`;
    });
  } catch (e) {
    console.warn('Failed to clear cookies:', e);
  }
};

/**
 * Get the appropriate login path based on user type
 */
export const getRoleLoginPath = () => {
  return '/login';
};

/**
 * Aggressive logout that clears everything and forces a hard reload
 * @param {Function} disconnectChatCallback - Optional callback to disconnect Stream Chat
 * @param {Object} options - Optional configuration
 * @param {string} options.redirectTo - Path to redirect to (defaults to role-specific login or home)
 * @param {string} options.reason - Reason for logout (e.g., 'session_expired')
 */
export const aggressiveLogout = (disconnectChatCallback = null, options = {}) => {
  if (typeof window === 'undefined') return;
  
  // Prevent multiple simultaneous logouts
  if (window.__logoutInProgress) return;
  window.__logoutInProgress = true;
  
  const { redirectTo, reason } = options;
  
  // Capture user type before clearing data (for redirect path)
  const loginPath = getRoleLoginPath();
  
  // Disconnect Stream Chat if callback provided
  if (disconnectChatCallback && typeof disconnectChatCallback === 'function') {
    try {
      disconnectChatCallback();
    } catch (e) {
      console.warn('Failed to disconnect chat:', e);
    }
  }
  
  // Clear all auth data
  clearAuthData();
  
  // Clear any other potential localStorage items with postaway prefix
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.toLowerCase().includes('postaway')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.warn('Failed to clear additional localStorage items:', e);
  }
  
  // Build redirect URL with cache busting
  const timestamp = Date.now();
  const targetPath = redirectTo || (reason === 'session_expired' ? loginPath : '/');
  const params = new URLSearchParams();
  params.set('logout', timestamp);
  params.set('nocache', timestamp);
  if (reason) {
    params.set('reason', reason);
  }
  
  // Force a hard redirect
  window.location.href = `${targetPath}?${params.toString()}`;
};

/**
 * Log current auth status to console (for debugging)
 */
export const logAuthStatus = () => {
  const token = getAuthToken();
  const userData = getUserData();
  const email = localStorage.getItem('postaway_email');
  const userType = localStorage.getItem('postaway_user_type');
  
  console.group('🔐 Authentication Status');
  console.log('Email:', email || 'Not set');
  console.log('User Type:', userType || 'Not set');
  console.log('User Data:', userData || 'Not set');
  
  if (token) {
    console.log('Token exists:', '✅');
    console.log('Token (first 20 chars):', token.substring(0, 20) + '...');
    
    const decoded = decodeToken(token);
    if (decoded) {
      console.log('Token Payload:', decoded);
      console.log('Token Expired:', isTokenExpired(token) ? '❌' : '✅');
      
      if (decoded.exp) {
        const expDate = new Date(decoded.exp * 1000);
        console.log('Expires at:', expDate.toLocaleString());
      }
    }
  } else {
    console.log('Token:', '❌ Not found');
  }
  
  console.groupEnd();
  
  return {
    token,
    userData,
    email,
    userType,
    isAuthenticated: !!token && !isTokenExpired(token)
  };
};

// Make this available globally for debugging in browser console
if (typeof window !== 'undefined') {
  window.checkAuth = logAuthStatus;
  window.getToken = getAuthToken;
  
  console.log('🔐 Auth helpers loaded! Use window.checkAuth() to see your authentication status.');
}
